import { useCallback, useState, type MouseEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type {
  AutoTransactionInfoDto,
  TransactionListDto,
} from "../../api/types";
import { invoicePath } from "../../logic/invoice-path";

export type AppliedRulesMenuController = ReturnType<typeof useAppliedRulesMenu>;

/**
 * State + handlers for the "applied rules" affordance shared by the desktop
 * and mobile transaction lists: clicking the bolt either opens the one rule
 * directly or a menu to pick among several, and editing a rule invalidates
 * the relevant queries on success. A stored rule opens in the rule editor; a
 * recorded invoice rule is generated, so — as on the details page — it opens
 * its invoice instead, and once the invoice is deleted there is nothing to
 * open (the menu shows it, flagged).
 */
export const useAppliedRulesMenu = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTransaction, setActiveTransaction] =
    useState<TransactionListDto | null>(null);

  // Stable: it's handed to memoized list rows, which must not re-render just
  // because this hook's owner did.
  const openAppliedRules = useCallback(
    (event: MouseEvent<HTMLElement>, transaction: TransactionListDto) => {
      const [only, ...rest] = transaction.appliedRules;
      if (only && rest.length === 0) {
        if (!only.invoice) {
          setEditingRuleId(only.id);
          return;
        }
        if (!only.broken) {
          navigate(invoicePath(only.invoice));
          return;
        }
      }
      setAnchorEl(event.currentTarget);
      setActiveTransaction(transaction);
    },
    // `navigate` changes only with the pathname, which the list never does.
    [navigate],
  );

  const closeMenu = () => {
    setAnchorEl(null);
    setActiveTransaction(null);
  };

  const openRule = (rule: AutoTransactionInfoDto) => {
    closeMenu();
    if (rule.invoice) {
      navigate(invoicePath(rule.invoice));
    } else {
      setEditingRuleId(rule.id);
    }
  };

  const closeEditing = () => setEditingRuleId(null);

  const handleRuleUpdated = () => {
    setEditingRuleId(null);
    queryClient.invalidateQueries({ queryKey: ["autoTransactionRules"] });
    queryClient.invalidateQueries({ queryKey: ["transactions"] });
  };

  return {
    editingRuleId,
    anchorEl,
    activeTransaction,
    openAppliedRules,
    closeMenu,
    openRule,
    closeEditing,
    handleRuleUpdated,
  };
};
